import queryString from "query-string";
import format from "date-fns/format";
import parse from "date-fns/parse";
import fetchPlatforms from "../../../platforms";
import { reportError } from "../../../appsignal";

class FragmentFetchError extends Error {
  constructor(message) {
    super(message);
  }

  get name() {
    return "FragmentFetchError";
  }
}

class FragmentGone extends Error {}

function parseResponseJSON(response) {
  if (response.status === 410) {
    throw new FragmentGone();
  }
  if (!response.ok) {
    throw new FragmentFetchError(`response not ok: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

async function buildFragmentURL({
  platformID,
  timestamp,
  duration,
  quality,
  mode
}) {
  return fetchPlatforms().then(platforms => {
    mode = mode || "i";
    const bandwidth = quality === "high" ? "h" : "l";
    const platform = platforms.find(p => p.id === platformID);
    const params = {
      m: mode, // i = information, r = redirect, b = both
      s: timestamp,
      l: duration,
      bw: bandwidth,
      p: platform.id
    };
    return `${platform.getclip_api_url}?${queryString.stringify(params)}`;
  });
}

function fetchFragmentURL({ platformID, timestamp, duration, mode }) {
  return buildFragmentURL({
    platformID,
    timestamp,
    duration,
    mode
  }).then(url => {
    return fetch(url, {
      method: "GET",
      mode: "cors",
      headers: {
        accept: "application/json"
      }
    });
  });
}

const GET_CLIP_DATE_TIME_FORMAT = "yyyyMMddHHmmss";

export default async function resolveFragment(item) {
  let duration;
  // Check if item is derived from a "shared_audio_item" (by virtue of its `_copy` property).
  if (item._copy != null) {
    const asDate = parse(
      item.data.timestamp,
      GET_CLIP_DATE_TIME_FORMAT,
      new Date()
    );
    // MUTATION!
    // Change timestamp to handle start_offset
    item.data.timestamp = format(
      asDate.getTime() + item.data.start_offset,
      GET_CLIP_DATE_TIME_FORMAT
    );
    // NB: duration is *already* correct here. It’s modified in `shared` by
    // virtue of copying it over the existing value
    duration = item.data.duration;
  } else {
    // Special-case broadcast episodes so that they request a duration of 0
    // even though they do have defined duration already. We do this so that the
    // fetch to find the length of a clip can default to the overlaps that the
    // media server natually defines.
    // <https://basecamp.com/1782196/projects/13043770/todos/360688155#events_todo_360688155>
    duration = item.type === "broadcast_episode" ? 0 : item.data.duration;
  }

  const platformID = item.data.platform_id;
  let timestamp = item.data.timestamp;

  try {
    const streamData = await fetchFragmentURL({
      platformID,
      timestamp,
      duration
    }).then(parseResponseJSON);
    let streams = {
      low: [],
      high: []
    };
    if (streamData.fragmented === "no") {
      const lowURL = await buildFragmentURL({
        platformID,
        timestamp,
        duration,
        quality: "low",
        mode: "r"
      });
      const highURL = await buildFragmentURL({
        platformID,
        timestamp,
        duration,
        quality: "high",
        mode: "r"
      });
      const baseData = {
        startTime: 0,
        duration
      };
      streams.low.push({ ...baseData, url: lowURL });
      streams.high.push({ ...baseData, url: highURL });
    } else {
      let startTime = 0;
      await Promise.all(
        streamData.fragments.map(async fragment => {
          const lowURL = await buildFragmentURL({
            platformID,
            timestamp: fragment.start,
            duration: fragment.length,
            quality: "low",
            mode: "r"
          });
          const highURL = await buildFragmentURL({
            platformID,
            timestamp: fragment.start,
            duration: fragment.length,
            quality: "high",
            mode: "r"
          });
          const baseData = {
            startTime,
            duration: fragment.length
          };
          // Increment startTime with current duration
          startTime = startTime + fragment.length;
          streams.low.push({ ...baseData, url: lowURL });
          streams.high.push({ ...baseData, url: highURL });
          return true;
        })
      );
    }
    return {
      ...item,
      streams
    };
  } catch (error) {
    if (!(error instanceof FragmentGone)) {
      reportError(error);
    }
    return { ...item };
  }
}
